import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOwnerView } from '../context/OwnerViewContext';
import api from '../services/api';
import './Sidebar.css';

const Sidebar = ({ isOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { viewMode, viewAgency, enterViewMode, exitViewMode } = useOwnerView();

  const isAdmin = user?.role === 'admin';
  const isDa = user?.role === 'da';
  const isOwner = user?.role === 'owner';
  const perms = user?.permissions || {};

  // Agency picker state for owner view mode
  const [showAgencyPicker, setShowAgencyPicker] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);

  const loadAgencies = async () => {
    setLoadingAgencies(true);
    try {
      const res = await api.get('/owner/agencies');
      setAgencies(res.data.data || []);
    } catch (e) {
      setAgencies([]);
    } finally {
      setLoadingAgencies(false);
    }
  };

  const handleEnterViewMode = (agency) => {
    enterViewMode(agency);
    setShowAgencyPicker(false);
    navigate('/');
  };

  const handleExitViewMode = () => {
    exitViewMode();
    navigate('/owner');
  };

  // Owner gets a completely different menu — system-wide management only
  const ownerMenuItems = [
    { path: '/owner', label: 'Owner Dashboard', icon: '👑' },
    { path: '/profile', label: 'Profile', icon: '👤' },
  ];

  const daMenuItems = [
    { path: '/requests', label: 'DA Requests', icon: '⚖️' },
    { path: '/profile', label: 'Profile', icon: '👤' }
  ];

  // Define all items with their required permission (or always/adminOnly flags)
  const allStandardItems = [
    { path: '/', label: 'Dashboard', icon: '📊', always: true },
    { path: '/cases', label: 'Cases', icon: '📁', permission: 'canCreateCases' },
    { path: '/evidence', label: 'Evidence', icon: '🔍', permission: 'canManageEvidence' },
    { path: '/documents', label: 'Documents', icon: '📄', permission: 'canManageDocuments' },
    { path: '/requests', label: 'DA Requests', icon: '⚖️', always: true },
    { path: '/timeline', label: 'Timeline / Audit', icon: '🔐', adminOnly: true },
    { path: '/personnel', label: 'Personnel', icon: '👥', permission: 'canManagePersonnel' },
    { path: '/analytics', label: 'Analytics', icon: '📈', permission: 'canViewAnalytics' },
    { path: '/search', label: 'Search', icon: '🔎', permission: 'canCreateCases' },
    { path: '/map', label: 'Map', icon: '🗺️', permission: 'canCreateCases' },
    { path: '/profile', label: 'Profile', icon: '👤', always: true },
    { path: '/compliance', label: 'Security', icon: '🛡️', always: true },
    { path: '/billing', label: 'Billing', icon: '💳', adminOnly: true },
    { path: '/export', label: 'Bulk Export', icon: '📦', adminOnly: true },
    { path: '/reports', label: 'Scheduled Reports', icon: '📅', adminOnly: true },
  ];

  const standardMenuItems = allStandardItems.filter(item => {
    if (item.adminOnly) return isAdmin || viewMode; // owner in view mode sees all
    if (item.always) return true;
    if (isAdmin || viewMode) return true;
    if (item.permission) return !!perms[item.permission];
    return true;
  });

  // In view mode, show the full agency menu; otherwise use role-based menu
  const menuItems = (isOwner && !viewMode) ? ownerMenuItems : isDa ? daMenuItems : standardMenuItems;

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
          >
            <span className="sidebar-icon">{item.icon}</span>
            <span className="sidebar-label">{item.label}</span>
          </Link>
        ))}

        {/* Owner View Mode toggle — only shown for owner */}
        {isOwner && !viewMode && (
          <button
            onClick={() => { setShowAgencyPicker(true); loadAgencies(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              width: '100%', padding: '10px 16px', marginTop: '8px',
              background: 'linear-gradient(135deg, #1e3a5f, #312e81)',
              border: '1px solid #4338ca', borderRadius: '8px',
              color: '#a5b4fc', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600,
              textAlign: 'left'
            }}
          >
            <span>👁</span>
            <span>View as Agency</span>
          </button>
        )}

        {/* Exit view mode button shown in sidebar when in view mode */}
        {viewMode && (
          <button
            onClick={handleExitViewMode}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              width: '100%', padding: '10px 16px', marginTop: '8px',
              background: 'linear-gradient(135deg, #92400e, #b45309)',
              border: '1px solid #f59e0b', borderRadius: '8px',
              color: '#fef3c7', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600,
              textAlign: 'left'
            }}
          >
            <span>←</span>
            <span>Exit View Mode</span>
          </button>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="user-role">
          {viewMode && viewAgency ? (
            <small style={{ color: '#f59e0b', fontWeight: 700, letterSpacing: '0.06em' }}>
              👁 VIEWING: {viewAgency.name?.toUpperCase()}
            </small>
          ) : isOwner ? (
            <small style={{ color: '#a78bfa', fontWeight: 700, letterSpacing: '0.08em' }}>SYSTEM OWNER</small>
          ) : (
            <small>{user?.role?.toUpperCase()}</small>
          )}
        </div>
      </div>

      {/* Agency Picker Modal */}
      {showAgencyPicker && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 20000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            background: '#1e293b', borderRadius: '14px', padding: '28px',
            width: '100%', maxWidth: '520px', maxHeight: '80vh',
            border: '1px solid #4338ca', display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.15rem' }}>👁 View as Agency</h2>
                <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '0.8rem' }}>
                  Select an agency to preview the full dashboard. Your session stays the same.
                </p>
              </div>
              <button onClick={() => setShowAgencyPicker(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.4rem', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingAgencies ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>Loading agencies...</div>
              ) : agencies.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>No agencies found.</div>
              ) : (
                agencies.map(agency => (
                  <button
                    key={agency._id}
                    onClick={() => handleEnterViewMode(agency)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      width: '100%', padding: '14px 16px', marginBottom: '8px',
                      background: '#0f172a', border: '1px solid #334155', borderRadius: '10px',
                      color: '#e2e8f0', cursor: 'pointer', textAlign: 'left',
                      transition: 'border-color 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#4338ca'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{agency.name}</div>
                      <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '2px' }}>
                        {agency.code} · {agency.address?.state || '—'} · {agency.billing?.planType || 'standard'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '0.72rem', padding: '2px 8px', borderRadius: '4px', fontWeight: 600,
                        background: agency.billing?.status === 'active' ? '#14532d' : '#1c2a1c',
                        color: agency.billing?.status === 'active' ? '#86efac' : '#4ade80',
                        border: `1px solid ${agency.billing?.status === 'active' ? '#16a34a' : '#166534'}`
                      }}>
                        {(agency.billing?.status || 'trialing').toUpperCase()}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.72rem', marginTop: '4px' }}>
                        {agency.stats?.totalUsers || 0} users
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
